import { zyStreamlit } from '@/lib/servers';
import { getStreamlitProductionUrl } from '@/lib/streamlit-utils';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 5000;
const POST_DNS_DELAY_MS = 10000;

async function getStreamlitDnsRecord(hostname: string): Promise<{ exists: boolean; modified_on?: string }> {
    try {
        const data = await zyStreamlit.get<{ success: boolean; exists: boolean; modified_on?: string }>('streamlit/dns_record', {
            params: { name: hostname },
        });
        return {
            exists: Boolean(data.exists),
            modified_on: data.modified_on,
        };
    } catch {
        return { exists: false };
    }
}

function postDnsDelayMs(modifiedOn?: string): number {
    if (!modifiedOn) {
        return POST_DNS_DELAY_MS;
    }

    const modifiedAt = Date.parse(modifiedOn);
    if (Number.isNaN(modifiedAt)) {
        return POST_DNS_DELAY_MS;
    }

    const ageMs = Date.now() - modifiedAt;
    return Math.max(0, Math.min(POST_DNS_DELAY_MS, POST_DNS_DELAY_MS - ageMs));
}

function isStreamlitAppReady(url: string): boolean {
    const request = new XMLHttpRequest();
    request.open('GET', url, false);

    try {
        request.send();
    } catch {
        return false;
    }

    return request.status === 200;
}

function delay(ms: number): Promise<void> {
    return new Promise(resolve => {
        window.setTimeout(resolve, ms);
    });
}

export interface StreamlitPreviewIframeProps {
    guid?: string;
    streamlitAppId?: number;
    className?: string;
    skipDnsPoll?: boolean;
}

export function StreamlitPreviewIframe({
    guid,
    streamlitAppId,
    className = 'h-96 w-full border-0',
    skipDnsPoll = false,
}: StreamlitPreviewIframeProps) {
    const [readyUrl, setReadyUrl] = useState<string | null>(null);

    const url = guid && streamlitAppId !== undefined && streamlitAppId !== null ? getStreamlitProductionUrl(guid, streamlitAppId) : null;
    const urlReady = Boolean(url && readyUrl === url);

    useEffect(() => {
        if (!url) {
            return;
        }

        let cancelled = false;

        const pollDns = async () => {
            if (cancelled) {
                return;
            }

            const hostname = new URL(url).hostname;
            const dnsRecord = await getStreamlitDnsRecord(hostname);
            if (cancelled) {
                return;
            }

            if (!dnsRecord.exists) {
                window.setTimeout(() => {
                    void pollDns();
                }, POLL_INTERVAL_MS);
                return;
            }

            await delay(postDnsDelayMs(dnsRecord.modified_on));
            if (cancelled) {
                return;
            }

            void pollApp();
        };

        const pollApp = async () => {
            if (cancelled) {
                return;
            }

            const ready = isStreamlitAppReady(url);
            if (cancelled) {
                return;
            }

            if (ready) {
                setReadyUrl(url);
                return;
            }

            window.setTimeout(() => {
                void pollApp();
            }, POLL_INTERVAL_MS);
        };

        if (skipDnsPoll) {
            void pollApp();
        } else {
            void pollDns();
        }

        return () => {
            cancelled = true;
        };
    }, [url, skipDnsPoll]);

    if (!url) {
        return (
            <div className="flex h-96 w-full flex-col items-center justify-center bg-zybooks-bg text-sm text-gray-500 dark:text-gray-400">
                <span>No Streamlit app detected.</span>
                <span>Edit and save this Streamlit app to create one.</span>
            </div>
        );
    }

    if (!urlReady) {
        return (
            <div className="flex h-96 w-full flex-col items-center justify-center gap-2 bg-zybooks-bg">
                <Loader2 className="text-muted-foreground mb-2 h-8 w-8 animate-spin" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Waiting for Streamlit app to be available</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    This will take up to 5 minutes. To preview your changes immediately, use "Run app" inside the streamlit app editor.
                </span>
            </div>
        );
    }

    return (
        <iframe
            src={url}
            className={className}
            title="Streamlit application"
            allowFullScreen
        />
    );
}
